/**
 * MARGIN TRADING — Conditional dropdown (Limit / Market / Conditional strip)
 * =============================================================================
 * Each `id` is stored in `infoPlaceOrder` on the margin page when the user picks
 * an item from the Conditional menu.
 *
 * - LIMIT / MARKET: top-level tabs (not listed here).
 * - Rows below: Conditional submenu only.
 *
 * `apiSupported === true` → existing spot `/spot/v1/orders` flow in `placeSpotOrder`.
 * `apiSupported === false` → separate React state + `MarginAdvancedFields.jsx` UI;
 *   submit shows “not wired” until backend contracts exist.
 */

/** @typedef {{ id: string, label: string, apiSupported: boolean, note?: string }} MarginConditionalMenuItem */

/** @type {MarginConditionalMenuItem[]} */
export const MARGIN_CONDITIONAL_DROPDOWN_ITEMS = [
  {
    id: "TP_SL",
    label: "TP/SL",
    apiSupported: false,
    note: "Take-profit / stop-loss branch — form state: marginFormTpSl*.",
  },
  {
    id: "TWAP",
    label: "TWAP",
    apiSupported: false,
    note: "Time-weighted slice — form state: marginFormTwap*.",
  },
  {
    id: "LOOP_ORDER",
    label: "Loop Order",
    apiSupported: false,
    note: "Loop order — form state: marginFormLoopOrder (price, priceMode, TP/SL %, pause, amount, totalLoops, validFor).",
  },
  {
    id: "TRAILING_STOP",
    label: "Trailing Stop",
    apiSupported: false,
    note: "Trailing stop — marginFormTrailingStop: pullback value + pullbackUnit (%|QUOTE), 5% chip, activation in quote only, amount, slider, validFor.",
  },
  {
    id: "ADVANCED_LIMIT",
    label: "Advanced Limit",
    apiSupported: false,
    note: "Post-only / display qty style controls — form state: marginFormAdvancedLimit*.",
  },
];

/** All `infoPlaceOrder` values that keep the Conditional split-button highlighted. */
export const MARGIN_CONDITIONAL_MENU_KIND_SET = new Set([
  "CONDITIONAL",
  ...MARGIN_CONDITIONAL_DROPDOWN_ITEMS.map((i) => i.id),
]);

/** Kinds that use the classic integrated order form (price / amount / sliders). */
export const MARGIN_CLASSIC_SPOT_FORM_KINDS = new Set(["LIMIT", "MARKET", "CONDITIONAL"]);

/** Kinds that call `AuthService.futuresPlaceOrder` via the futures API. */
export const MARGIN_SPOT_API_ORDER_KINDS = new Set(["LIMIT", "MARKET", "CONDITIONAL"]);

export function marginConditionalMenuLabel(orderKind) {
  const hit = MARGIN_CONDITIONAL_DROPDOWN_ITEMS.find((x) => x.id === orderKind);
  return hit?.label ?? "Conditional";
}

/** Conditional uses `MarginConditionalFields`, not the classic stop row. */
export function marginOrderUsesStopTriggerField(_orderKind) {
  return false;
}

export function marginOrderUsesMarketPriceReadonly(orderKind) {
  return orderKind === "MARKET";
}

/** Amount denom selector (BASE vs QUOTE) — same rules as classic stop-limit. */
export function marginShowAmtDenomSelect(orderKind) {
  return orderKind === "MARKET";
}
